import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  Bell,
  CalendarClock,
  Globe,
  LayoutDashboard,
  LogOut,
  Menu,
  Users,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/Button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { cn } from "@/lib/utils";
import { getCachedUser, getCurrentUser, logout } from "@/lib/auth";
import type { User } from "@/lib/api";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Дашборд", icon: LayoutDashboard, end: true },
  { to: "/sites", label: "Сайти", icon: Globe },
  { to: "/channels", label: "Канали", icon: Bell },
  { to: "/incidents", label: "Інциденти", icon: AlertTriangle },
  { to: "/maintenance", label: "Обслуговування", icon: CalendarClock },
  { to: "/users", label: "Користувачі", icon: Users },
];

export function Layout(): JSX.Element {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(getCachedUser());
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void getCurrentUser().then((u) => {
      if (!cancelled) setUser(u);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onLogout = (): void => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Mobile overlay */}
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      ) : null}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-64 transform border-r bg-card transition-transform md:static md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-16 items-center justify-between border-b px-4">
          <Link
            to="/"
            className="flex items-center gap-2 font-semibold"
            onClick={() => setMobileOpen(false)}
          >
            <Activity className="h-5 w-5 text-primary" />
            <span>URL Sensor</span>
          </Link>
          <button
            type="button"
            className="md:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label="Закрити меню"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex flex-col gap-1 p-3">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-accent hover:text-accent-foreground",
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b bg-background/95 px-4 backdrop-blur md:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="md:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Відкрити меню"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="hidden text-sm sm:flex sm:flex-col sm:items-end">
              <span className="font-medium">{user?.email ?? "—"}</span>
              {user ? (
                <span className="text-xs text-muted-foreground">
                  {user.role}
                </span>
              ) : null}
            </div>
            <ThemeToggle />
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              aria-label="Вийти"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Вийти</span>
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
